import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  parseBoolean,
  ParsedExcelData,
  parseNumber,
  ValidationError
} from '@/lib/excel';
import { generateSoChungTuHienTai } from '@/lib/stringUtil';
import { MA_CHUNG_TU } from '@/constants';

const HEADERS = {
  // Main fields
  SO_CT: 'Số chứng từ*',
  SO_CT_HDDT: 'Số hóa đơn',
  NGAY_LCT: 'Ngày hạch toán*',
  NGAY_CT_HDDT: 'Ngày hóa đơn',
  MA_NT: 'Ngoại tệ*',
  PX: 'Xuất kho',
  TY_GIA: 'Tỷ giá',
  MA_TTHDDT: 'Trạng thái HĐĐT*',
  THANH_TOAN: 'Thanh toán',
  SO_CT2_HDDT: 'Ký hiệu',
  MA_MAU_CT_HDDT: 'Mẫu hóa đơn',
  SO_PX: 'Số phiếu xuất',
  NGAY_PX: 'Ngày xuất kho',
  HDBH_YN: 'Hoá đơn',
  CK_YN: 'Chiết khấu',
  MA_KH: 'Mã khách hàng*',
  TEN_KH_THUE: 'Tên khách/Ng.mua',
  MA_SO_THUE: 'Mã số thuế',
  DIA_CHI: 'Địa chỉ',
  ONG_BA: 'Người nhận',
  MA_NVBH: 'Mã nhân viên',
  TK: 'Tài khoản nợ*',
  DIEN_GIAI: 'Diễn giải',
  TKNH: 'Ngân hàng th.toán',
  MA_GD: 'Giao dịch',
  CHUYEN_DU_LIEU: 'Chuyển dữ liệu',
  TRANG_THAI: 'Trạng thái',
  // Detail fields
  MA_VT: 'Mã sản phẩm*',
  TEN_VT: 'Tên sản phẩm',
  DVT: 'Đvt*',
  MA_KHO: 'Mã kho*',
  MA_LO: 'Mã lô',
  MA_VI_TRI: 'Mã vị trí',
  CT_KM: 'Loại hàng',
  SO_LUONG: 'Số lượng',
  GIA_NT2: 'Giá bán',
  TIEN_NT2: 'Thành tiền ',
  TL_CK: 'Tl ck(%)',
  CK_NT: 'Ch.khấu ',
  PX_DD: 'Đích danh',
  GIA_NT: 'Giá tồn ',
  TIEN_NT: 'Tiền ',
  THUE_SUAT: 'Thuế suất*',
  TK_THUE_CO: 'Tk thuế có*',
  THUE_NT: 'Thuế ',
  TK_DT: 'Tk doanh thu*',
  TK_GV: 'Tk giá vốn*',
  TK_VT: 'Tk kho*',
  TK_CK: 'Tk chiết khấu',
  TK_KM: 'Tk khuyến mãi',
  GHI_CHU: 'Ghi chú/ Tên hàng hóa HĐĐT',
  MA_BP: 'Bộ phận',
  MA_VV: 'Vụ việc',
  MA_HD: 'Hợp đồng',
  MA_DTT: 'Đợt thanh toán',
  MA_KU: 'Khế ước',
  MA_PHI: 'Phí',
  MA_SP: 'Sản phẩm',
  MA_LSX: 'Lệnh sản xuất',
  MA_CP0: 'C/p không h/lệ'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.NGAY_LCT, key: 'ngay_lct', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.SO_CT_HDDT, key: 'so_ct_hddt0', type: 'main' },
  { header: HEADERS.MA_TTHDDT, key: 'ma_tthddt', type: 'main' },
  { header: HEADERS.THANH_TOAN, key: 'pt_tao_yn', type: 'main' },
  { header: HEADERS.NGAY_CT_HDDT, key: 'ngay_ct_hddt0', type: 'main' },
  { header: HEADERS.SO_CT2_HDDT, key: 'so_ct2_hddt', type: 'main' },
  { header: HEADERS.MA_MAU_CT_HDDT, key: 'ma_mau_ct_hddt', type: 'main' },
  { header: HEADERS.PX, key: 'px_yn', type: 'main' },
  { header: HEADERS.SO_PX, key: 'so_px', type: 'main' },
  { header: HEADERS.NGAY_PX, key: 'ngay_px', type: 'main' },
  { header: HEADERS.HDBH_YN, key: 'hdbh_yn', type: 'main' },
  { header: HEADERS.CK_YN, key: 'ck_yn', type: 'main' },
  { header: HEADERS.MA_KH, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KH_THUE, key: 'ten_kh_thue', type: 'main' },
  { header: HEADERS.MA_SO_THUE, key: 'ma_so_thue', type: 'main' },
  { header: HEADERS.DIA_CHI, key: 'dia_chi', type: 'main' },
  { header: HEADERS.ONG_BA, key: 'ong_ba', type: 'main' },
  { header: HEADERS.MA_NVBH, key: 'ma_nvbh', type: 'main' },
  { header: HEADERS.TK, key: 'tk', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },
  { header: HEADERS.TKNH, key: 'tknh', type: 'main' },
  { header: HEADERS.MA_GD, key: 'ma_gd', type: 'main' },
  { header: HEADERS.CHUYEN_DU_LIEU, key: 'transfer_yn', type: 'main' },
  // Detail fields
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.CT_KM, key: 'ct_km', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.GIA_NT2, key: 'gia_nt2', type: 'chi_tiet' },
  { header: HEADERS.TIEN_NT2, key: 'tien_nt2', type: 'chi_tiet' },
  { header: HEADERS.TL_CK, key: 'tl_ck', type: 'chi_tiet' },
  { header: HEADERS.CK_NT, key: 'ck_nt', type: 'chi_tiet' },
  { header: HEADERS.PX_DD, key: 'px_dd', type: 'chi_tiet' },
  { header: HEADERS.GIA_NT, key: 'gia_nt', type: 'chi_tiet' },
  { header: HEADERS.TIEN_NT, key: 'tien_nt', type: 'chi_tiet' },
  { header: HEADERS.THUE_SUAT, key: 'thue_suat', type: 'chi_tiet' },
  { header: HEADERS.TK_THUE_CO, key: 'tk_thue_co', type: 'chi_tiet' },
  { header: HEADERS.THUE_NT, key: 'thue_nt', type: 'chi_tiet' },
  { header: HEADERS.TK_DT, key: 'tk_dt', type: 'chi_tiet' },
  { header: HEADERS.TK_GV, key: 'tk_gv', type: 'chi_tiet' },
  { header: HEADERS.TK_VT, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.TK_CK, key: 'tk_ck', type: 'chi_tiet' },
  { header: HEADERS.TK_KM, key: 'tk_km', type: 'chi_tiet' },
  { header: HEADERS.GHI_CHU, key: 'ghi_chu', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.MA_VV, key: 'ma_vv', type: 'chi_tiet' },
  { header: HEADERS.MA_HD, key: 'ma_hd', type: 'chi_tiet' },
  { header: HEADERS.MA_DTT, key: 'ma_dtt', type: 'chi_tiet' },
  { header: HEADERS.MA_KU, key: 'ma_ku', type: 'chi_tiet' },
  { header: HEADERS.MA_PHI, key: 'ma_phi', type: 'chi_tiet' },
  { header: HEADERS.MA_SP, key: 'ma_sp', type: 'chi_tiet' },
  { header: HEADERS.MA_LSX, key: 'ma_lsx', type: 'chi_tiet' },
  { header: HEADERS.MA_CP0, key: 'ma_cp0', type: 'chi_tiet' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_kh', 'ma_nvbh', 'tk', 'ma_gd'],
  chi_tiet: [
    'ma_vt',
    'dvt',
    'ma_kho',
    'ma_lo',
    'ma_vi_tri',
    'ma_thue',
    'tk_vt',
    'tk_dt',
    'tk_gv',
    'tk_ck',
    'tk_thue_co',
    'tk_km',
    'ma_bp',
    'ma_vv',
    'ma_hd',
    'ma_dtt',
    'ma_ku',
    'ma_phi',
    'ma_cp0'
  ]
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const getFieldValues = <T>(...candidates: Array<T[] | undefined>): T[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const hasMeaningfulValue = candidate.some(
        value => value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (hasMeaningfulValue) {
        return candidate;
      }
    }
  }
  return [];
};

const hasData = (item: BanHangSearchParams, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, BanHangRecord[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};

  const seenDates = new Set<string>();
  const chungTuArr: Array<{ ma_ct: string; ngay_hl: string }> = [];

  (main.so_ct || []).forEach((_: BanHangValue, index: number) => {
    const ngay =
      convertExcelDate(main.ngay_lct?.[index]) ||
      convertExcelDate(main.ngay_ct?.[index]) ||
      format(new Date(), 'yyyy-MM-dd');

    const ngay_hl = ngay || format(new Date(), 'yyyy-MM-dd');
    if (seenDates.has(ngay_hl)) return;
    seenDates.add(ngay_hl);

    chungTuArr.push({
      ma_ct: MA_CHUNG_TU.TON_KHO.PHIEU_XUAT_KHO,
      ngay_hl
    });
  });

  return {
    main: {
      ...build(main, UUID_FIELDS.main),
      ma_gd: (main.ma_gd || []).map((val: BanHangValue) => (val ? `HD1${val}` : val))
    },
    chi_tiet: {
      ...build(chi_tiet, UUID_FIELDS.chi_tiet),
      ma_thue: getFieldValues(chi_tiet.thue_suat, chi_tiet.ma_thue)
    },
    chung_tu: chungTuArr
  };
};

const ValidateUUID = (response: BanHangRecord, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const calculateTotals = (chiTiet: ImportBody['chi_tiet']) => {
  let t_so_luong = 0;
  let t_tien_nt2 = 0;
  let t_tien2 = 0;
  let t_thue_nt = 0;
  let t_thue = 0;
  let t_ck_nt = 0;
  let t_ck = 0;
  let t_tien_nt = 0;
  let t_tien = 0;

  chiTiet.forEach(item => {
    t_so_luong += item.so_luong || 0;
    t_tien_nt2 += item.tien_nt2 || 0;
    t_tien2 += item.tien_nt2 || 0;
    t_thue_nt += item.thue_nt || 0;
    t_thue += item.thue_nt || 0;
    t_ck_nt += item.ck_nt || 0;
    t_ck += item.ck_nt || 0;
    t_tien_nt += item.tien_nt || 0;
    t_tien += item.tien_nt || 0;
  });

  const t_tt_nt = t_tien_nt2 + t_thue_nt - t_ck_nt;
  const t_tt = t_tien2 + t_thue - t_ck;

  return {
    t_so_luong,
    t_tien_nt2,
    t_tien2,
    t_thue_nt,
    t_thue,
    t_ck_nt,
    t_ck,
    t_tt_nt,
    t_tt,
    t_tien_nt,
    t_tien
  };
};

const processMainRows = (
  main: Record<string, BanHangRecord[]>,
  UUIDResponse: BanHangRecord,
  entityUnitUUID: string,
  rowCount: number,
  defaultMaGD: string
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();
  const deliveryNoteCounters = new Map<string, number>();
  const voucherArray = Array.isArray(UUIDResponse?.chung_tu)
    ? UUIDResponse.chung_tu
    : UUIDResponse?.chung_tu
      ? [UUIDResponse.chung_tu]
      : [];

  const voucherByDate = new Map<string, BanHangRecord>();
  voucherArray.forEach((voucher: BanHangRecord) => {
    const normalizedDate = voucher?.ngay_hl?.slice(0, 10);
    if (!normalizedDate) return;
    const existing = voucherByDate.get(normalizedDate);
    if (!existing || Number(voucher.i_so_ct_ht ?? 0) > Number(existing.i_so_ct_ht ?? 0)) {
      voucherByDate.set(normalizedDate, voucher);
    }
  });

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = main.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const isTransfer = parseBoolean(main.transfer_yn?.[i]);
      const isInvoice = parseBoolean(main.hdbh_yn?.[i]);
      const rawDiscountFlag = main.ck_yn?.[i];
      const hasDiscount =
        rawDiscountFlag !== undefined && rawDiscountFlag !== null && String(rawDiscountFlag).trim() !== ''
          ? String(rawDiscountFlag).trim() !== '0'
          : parseBoolean(rawDiscountFlag);
      const shouldCreatePayment = parseBoolean(main.pt_tao_yn?.[i]);

      const transactionDate = convertExcelDate(main.ngay_lct?.[i]) || convertExcelDate(main.ngay_ct?.[i]);
      const documentDate = convertExcelDate(main.ngay_ct?.[i]) || transactionDate;
      const effectiveDate = transactionDate || documentDate || format(new Date(), 'yyyy-MM-dd');

      const voucherInfo = voucherByDate.get(effectiveDate) || voucherArray[i] || voucherArray[0];

      const counterKey = `${voucherInfo?.chung_tu || voucherInfo?.ma_ct || 'default'}_${effectiveDate}`;
      const base = voucherInfo?.i_so_ct_ht !== undefined ? Number(voucherInfo.i_so_ct_ht) : 0;
      const nextNumber = deliveryNoteCounters.has(counterKey) ? deliveryNoteCounters.get(counterKey)! : base + 1;
      deliveryNoteCounters.set(counterKey, nextNumber + 1);
      const nextDeliveryNoteNumber = voucherInfo ? nextNumber : undefined;
      const generatedDeliveryNoteNumber =
        voucherInfo?.so_ct_mau && nextDeliveryNoteNumber
          ? generateSoChungTuHienTai(nextDeliveryNoteNumber, voucherInfo.so_ct_mau, effectiveDate)
          : undefined;

      groupedRecords.set(recordKey, {
        hdbh_yn: isInvoice,
        px_yn: true,
        pt_tao_yn: shouldCreatePayment,
        pt_yn: false,
        ck_yn: hasDiscount,
        loai_ck: hasDiscount ? '1' : '4',
        ck_tl_nt: 0,
        so_ct: documentNumber || undefined,
        ngay_ct: transactionDate || documentDate,
        ngay_lct: transactionDate || documentDate,
        i_so_ct: 0,
        so_px: generatedDeliveryNoteNumber || main.so_px?.[i] || undefined,
        ngay_px: convertExcelDate(main.ngay_px?.[i]),
        i_so_px: nextDeliveryNoteNumber,
        ma_nk_px: voucherInfo?.ma_nk || undefined,
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || main.ma_nt?.[i] || '',
        ty_gia: main.ty_gia?.[i] || '1',
        ngay_ct_hddt0: convertExcelDate(main.ngay_ct_hddt0?.[i]) || undefined,
        so_ct_hddt0: main.so_ct_hddt0?.[i] || undefined,
        px_info: {
          so_ct: generatedDeliveryNoteNumber || main.so_px?.[i] || undefined,
          i_so_ct: nextDeliveryNoteNumber,
          ma_nk: voucherInfo?.ma_nk || undefined,
          so_ct_mau: voucherInfo?.so_ct_mau,
          i_so_ct_ht: voucherInfo?.i_so_ct_ht,
          chung_tu: voucherInfo?.chung_tu
        },
        ma_kh: UUIDResponse.main?.ma_kh?.[i] || '',
        ten_kh_thue: main.ten_kh_thue?.[i] || undefined,
        ma_so_thue: main.ma_so_thue?.[i] || undefined,
        dia_chi: main.dia_chi?.[i] || undefined,
        ong_ba: main.ong_ba?.[i] || undefined,
        dien_giai: (main.dien_giai?.[i]?.trim?.() || '') as string,
        ma_nvbh: UUIDResponse.main?.ma_nvbh?.[i] || '',
        tk: UUIDResponse.main?.tk?.[i] || '',
        tknh: main.tknh?.[i] || undefined,
        ma_tthddt: main.ma_tthddt?.[i] || '1',
        ma_mau_ct_hddt: main.ma_mau_ct_hddt?.[i] || undefined,
        ma_tt: UUIDResponse.main?.ma_tt?.[i] || '',
        transfer_yn: isTransfer,
        unit_id: entityUnitUUID,
        ma_ngv: '1',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || main.ma_gd?.[i] || defaultMaGD,
        status: '5',
        ngay_bk: convertExcelDate(main.ngay_bk?.[i]) || undefined,
        ma_httt: main.ma_httt?.[i] || 'TMB',
        e_mail: main.e_mail?.[i] || undefined,
        ma_dc: UUIDResponse.main?.ma_dc?.[i] || undefined,
        ma_ptvc: UUIDResponse.main?.ma_ptvc?.[i] || undefined,
        ma_ptgh: UUIDResponse.main?.ma_ptgh?.[i] || undefined,
        ma_kh9: UUIDResponse.main?.ma_kh9?.[i] || undefined,
        so_ct2: main.so_ct2?.[i] || undefined,
        so_ct0: main.so_ct0?.[i] || undefined,
        so_ct2_hddt: main.so_ct2_hddt?.[i] || undefined,
        t_so_luong: 0,
        t_tien_nt2: 0,
        t_tien2: 0,
        t_thue_nt: 0,
        t_thue: 0,
        t_ck_nt: 0,
        t_ck: 0,
        t_tt_nt: 0,
        t_tt: 0,
        t_tien_nt: 0,
        t_tien: 0,
        chi_tiet: []
      });
    }
  }

  return groupedRecords;
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  main: Record<string, BanHangRecord[]>,
  chi_tiet: Record<string, BanHangRecord[]>,
  UUIDResponse: BanHangRecord,
  rowCount: number
): Map<string, ImportBody['chi_tiet']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet']>();
  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = main.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    if ((!record.dien_giai || record.dien_giai.trim() === '') && main.dien_giai?.[i]?.trim?.()) {
      record.dien_giai = String(main.dien_giai[i]).trim();
    }

    const quantity = parseNumber(chi_tiet.so_luong?.[i]);
    const price2 = parseNumber(chi_tiet.gia_nt2?.[i]);
    const amount2 = parseNumber(chi_tiet.tien_nt2?.[i]) || quantity * price2;
    const cost = parseNumber(chi_tiet.gia_nt?.[i]);
    const directDelivery =
      String(chi_tiet.px_dd?.[i] || '').toLowerCase() === 'true' ||
      String(chi_tiet.px_dd?.[i] || '').toLowerCase() === '1';
    const productType = chi_tiet.ct_km?.[i] || '0';

    const discountRate = parseNumber(chi_tiet.tl_ck?.[i]);
    const discount = parseNumber(chi_tiet.ck_nt?.[i]);

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const taxRate = parseNumber(chi_tiet.thue_suat?.[i]);
    const taxCode = UUIDResponse.chi_tiet?.ma_thue?.[i] || undefined;

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || '',
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || '',
      ma_kho: UUIDResponse.chi_tiet?.ma_kho?.[i] || '',
      ma_lo: chi_tiet.ma_lo?.[i] || undefined,
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || chi_tiet.ma_vi_tri?.[i] || undefined,
      so_luong: quantity,
      sl_cl: quantity,
      gia_nt2: price2,
      tien_nt2: amount2,
      tl_ck: discountRate || undefined,
      ck_nt: discount || undefined,
      ma_thue: taxCode,
      thue_suat: taxRate,
      thue_nt: parseNumber(chi_tiet.thue_nt?.[i]),
      tk_thue_co: UUIDResponse.chi_tiet?.tk_thue_co?.[i] || undefined,
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || undefined,
      ma_vv: UUIDResponse.chi_tiet?.ma_vv?.[i] || undefined,
      ma_hd: UUIDResponse.chi_tiet?.ma_hd?.[i] || undefined,
      ma_dtt: UUIDResponse.chi_tiet?.ma_dtt?.[i] || undefined,
      ma_ku: UUIDResponse.chi_tiet?.ma_ku?.[i] || undefined,
      ma_phi: UUIDResponse.chi_tiet?.ma_phi?.[i] || undefined,
      ma_cp0: UUIDResponse.chi_tiet?.ma_cp0?.[i] || undefined,
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || undefined,
      tk_dt: UUIDResponse.chi_tiet?.tk_dt?.[i] || undefined,
      tk_gv: UUIDResponse.chi_tiet?.tk_gv?.[i] || undefined,
      tk_ck: UUIDResponse.chi_tiet?.tk_ck?.[i] || undefined,
      tk_km: UUIDResponse.chi_tiet?.tk_km?.[i] || undefined,
      px_dd: directDelivery || false,
      gia_nt: cost || 0,
      tien_nt: parseNumber(chi_tiet.tien_nt?.[i]),
      ct_km: productType,
      ghi_chu: chi_tiet.ghi_chu?.[i] || undefined
    };

    if (hasData(detailItem, detailKeys)) {
      detailsForKey.push(detailItem);
      chiTietMap.set(recordKey, detailsForKey);
    }
  }

  return chiTietMap;
};

const TransformToBodies = (
  dataRows: BanHangRecord[][],
  parsedData: ParsedExcelData,
  uuidResponse: BanHangRecord,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData.main || {};
  const chi_tiet = parsedData.chi_tiet || {};
  const rowCount = dataRows.length;
  const defaultMaGD = context.defaultMaGD || 'HD';

  const groupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount, defaultMaGD);
  const chiTietMap = processChiTietRows(groupedRecords, main, chi_tiet, uuidResponse, rowCount);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`;
    record.chi_tiet = chiTietMap.get(key) || [];

    const totals = calculateTotals(record.chi_tiet);
    record.t_so_luong = totals.t_so_luong;
    record.t_tien_nt2 = totals.t_tien_nt2;
    record.t_tien2 = totals.t_tien2;
    record.t_thue_nt = totals.t_thue_nt;
    record.t_thue = totals.t_thue;
    record.t_ck_nt = totals.t_ck_nt;
    record.t_ck = totals.t_ck;
    record.t_tt_nt = totals.t_tt_nt;
    record.t_tt = totals.t_tt;
    record.t_tien_nt = totals.t_tien_nt || totals.t_tien_nt2;
    record.t_tien = totals.t_tien || totals.t_tien2;
  });

  return records;
};

export interface ImportBody {
  hdbh_yn?: boolean;
  px_yn?: boolean;
  pt_yn?: boolean;
  pt_tao_yn?: boolean;
  ck_yn?: boolean;
  loai_ck?: string;
  ck_tl_nt?: number;
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ma_nt?: string;
  ty_gia?: string;
  ngay_ct_hddt0?: string;
  so_ct_hddt0?: string;
  so_px?: string;
  ngay_px?: string;
  ma_nk_px?: string;
  i_so_px?: number;
  ma_kh: string;
  ten_kh_thue?: string;
  ma_so_thue?: string;
  dia_chi?: string;
  ong_ba?: string;
  dien_giai?: string;
  ma_nvbh?: string;
  tk?: string;
  tknh?: string;
  ma_tthddt?: string;
  ma_mau_ct_hddt?: string;
  ma_tt?: string;
  transfer_yn?: boolean;
  unit_id: string;
  ma_ngv: string;
  ma_gd?: string;
  status?: string;
  i_so_ct?: number;
  px_info?: {
    so_ct?: string;
    i_so_ct?: number;
    ma_nk?: string;
    so_ct_mau?: string;
    i_so_ct_ht?: number;
    chung_tu?: string;
  };
  ngay_bk?: string;
  ma_httt?: string;
  e_mail?: string;
  ma_dc?: string;
  ma_ptvc?: string;
  ma_ptgh?: string;
  ma_kh9?: string;
  so_ct2?: string;
  so_ct0?: string;
  so_ct2_hddt?: string;
  t_so_luong?: number;
  t_tien_nt2?: number;
  t_tien2?: number;
  t_thue_nt?: number;
  t_thue?: number;
  t_ck_nt?: number;
  t_ck?: number;
  t_tt_nt?: number;
  t_tt?: number;
  t_tien_nt?: number;
  t_tien?: number;
  chi_tiet: Array<{
    line: number;
    ma_vt: string;
    dvt: string;
    ma_kho: string;
    ma_lo?: string;
    ma_vi_tri?: string;
    so_luong: number;
    gia_nt2: number;
    tien_nt2: number;
    tl_ck?: number;
    ck_nt?: number;
    ma_thue?: string;
    thue_suat?: number;
    thue_nt?: number;
    tk_thue_co?: string;
    ma_bp?: string;
    ma_vv?: string;
    ma_hd?: string;
    ma_dtt?: string;
    ma_ku?: string;
    ma_phi?: string;
    ma_cp0?: string;
    tk_vt?: string;
    tk_dt?: string;
    tk_gv?: string;
    tk_ck?: string;
    tk_km?: string;
    px_dd?: boolean;
    gia_nt?: number;
    tien_nt?: number;
    sl_cl?: number;
    ct_km?: string;
    ghi_chu?: string;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
